import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, CaretDown } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import { ACCENT, api, FRAMEWORK_META } from "@/lib/api"; // eslint-disable-line no-unused-vars
import { EmptyState, StateBadge } from "@/components/shared";
import { fmtPeriod, NOT_ASSESSED } from "@/lib/period";
import { DINACHARYA_DEMO } from "@/lib/demoScores";
import { useDinacharyaScores } from "@/lib/dinacharyaScores";

const TABS = [
  ["pending", "Pending"],
  ["overdue", "Overdue"],
  ["scores", "Recent scores"],
];

const CARD = {
  dinacharya: {
    ink: "text-[hsl(186_82%_24%)]", ring: "ring-[hsl(186_74%_32%)]/25",
    wash: "bg-gradient-to-b from-[hsl(186_70%_95%)] via-white to-white",
    halo: "from-[hsl(186_74%_38%)]/18 to-transparent",
    btn: "bg-[hsl(186_78%_28%)] hover:bg-[hsl(186_82%_22%)]",
    rule: "via-[hsl(186_74%_34%)]/55",
  },
  panchatanthra: {
    ink: "text-[hsl(18_90%_40%)]", ring: "ring-[hsl(18_88%_45%)]/25",
    wash: "bg-gradient-to-b from-[hsl(34_92%_94%)] via-white to-white",
    halo: "from-[hsl(30_92%_54%)]/20 to-transparent",
    btn: "bg-[hsl(18_88%_45%)] hover:bg-[hsl(16_88%_38%)]",
    rule: "via-[hsl(24_88%_50%)]/60",
  },
  viveka_kartha: {
    ink: "text-[hsl(290_58%_30%)]", ring: "ring-[hsl(288_52%_42%)]/25",
    wash: "bg-gradient-to-b from-[hsl(288_58%_95%)] via-white to-white",
    halo: "from-[hsl(288_52%_50%)]/18 to-transparent",
    btn: "bg-[hsl(288_52%_40%)] hover:bg-[hsl(290_58%_32%)]",
    rule: "via-[hsl(288_48%_46%)]/55",
  },
};

const Ornament = ({ tone }) => (
  <div className="mt-3 flex items-center justify-center gap-2">
    <span className={`h-px w-14 bg-gradient-to-r from-transparent ${tone}`} />
    <span className={`text-[9px] ${tone.replace("via-", "text-").replace("/40", "").replace("/45", "")}`}>❖</span>
    <span className={`h-px w-14 bg-gradient-to-l from-transparent ${tone}`} />
  </div>
);

export default function Home() {
  const { tenant, tenantId, access } = useAuth();
  const { scores: dcScores } = useDinacharyaScores();
  const [work, setWork] = useState({ items: [], exceptions: [] });
  const [snapshots, setSnapshots] = useState([]);
  const [scorecard, setScorecard] = useState({});
  const [period, setPeriod] = useState(null);
  const [tab, setTab] = useState("pending");
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!tenantId) return;
    api.get(`/tenants/${tenantId}/scorecard`, { params: period ? { period } : {} })
      .then(({ data }) => setScorecard(data)).catch(() => setScorecard({}));
  }, [tenantId, period]);

  useEffect(() => {
    api.get("/my-work").then(({ data }) => setWork(data)).catch(() => {});
  }, [tenantId]);

  useEffect(() => {
    if (!tenantId || !access?.frameworks?.includes("panchatanthra")) return;
    api.get(`/tenants/${tenantId}/panchatanthra/snapshots`)
      .then(({ data }) => setSnapshots(data)).catch(() => {});
  }, [tenantId, access]);

  const counts = useCallback(
    (key) => ({ pending: work.items.length, overdue: work.exceptions.length, scores: snapshots.length }[key]),
    [work, snapshots]
  );

  if (!tenantId)
    return (
      <EmptyState
        testid="home-no-tenant"
        title="No engagement assigned"
        message="Your account has no engagement grants yet. An Aikyam Admin must assign a scoped grant before you can work."
      />
    );

  return (
    <div className="space-y-10">
      {/* Tight header — the frameworks start high on the screen */}
      <div className="flex flex-wrap items-end justify-between gap-4" data-testid="home-header">
        <div>
          <p className="label-caps text-goldink">Family Business 360°</p>
          <h1 className="mt-1.5 font-display text-3xl leading-tight text-navy sm:text-4xl">
            {tenant?.name || "Engagement"}
          </h1>
          <p className="mt-1.5 text-sm text-muted-foreground">
            {tenant?.industry || "Family business"}
            {scorecard.selected_period ? ` · ${fmtPeriod(period || scorecard.selected_period)}` : ""}
            {" · Three perspectives, one purpose"}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {scorecard.periods?.length > 0 && (
            <select
              data-testid="home-period-select"
              value={period || scorecard.selected_period || ""}
              onChange={(e) => setPeriod(e.target.value)}
              className="h-10 rounded-full border border-border bg-card px-5 text-xs text-navy transition-colors hover:border-gold"
            >
              {scorecard.periods.map((p) => <option key={p} value={p}>{fmtPeriod(p)}</option>)}
            </select>
          )}
        </div>
      </div>

      {/* The proprietary framework owns the first screen */}
      <section data-testid="home-scorecard">
        <div className="stagger grid grid-cols-1 gap-6 md:grid-cols-3 md:gap-8">
          {(access?.frameworks || []).map((fw) => {
            const meta = FRAMEWORK_META[fw];
            const c = CARD[fw];
            const raw = scorecard.frameworks?.[fw];
            const s = fw !== "dinacharya" ? raw
              : dcScores?.team_score != null
                ? { ...raw, score10: dcScores.team_score, band: dcScores.team_band }
                : raw?.score10 == null ? { ...raw, ...DINACHARYA_DEMO } : raw;
            return (
              <Link
                key={fw}
                to={`/${meta.path}`}
                data-testid={`framework-card-${fw}`}
                className={`panel group flex h-full min-h-[36rem] flex-col items-center overflow-hidden px-8 py-10 text-center ring-1 ${c.ring} ${c.wash} transition-[transform,box-shadow] duration-700 ease-[cubic-bezier(0.22,1,0.36,1)] hover:-translate-y-2 hover:shadow-elevated`}
              >
                <div className={`relative grid h-40 w-40 shrink-0 place-items-center overflow-hidden rounded-full bg-white shadow-[0_10px_34px_-18px_rgba(10,22,45,0.4)] ring-1 ${c.ring} lg:h-48 lg:w-48`}>
                  <img
                    src={meta.icon}
                    alt={`${meta.name} icon`}
                    data-testid={`framework-icon-${fw}`}
                    className="relative z-10 h-[85%] w-[85%] rounded-full object-cover transition-transform duration-700 ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-105"
                    style={{ filter: "saturate(1.22) contrast(1.06)" }}
                  />
                  <span className={`pointer-events-none absolute -inset-3 -z-10 rounded-full bg-gradient-to-br ${c.halo} opacity-70 blur-xl`} />
                </div>

                <h2 className={`mt-8 w-full break-words px-1 font-display uppercase leading-[1.05] tracking-[0.03em] ${c.ink}`}
                    style={{ fontSize: "clamp(1.35rem, 2.05vw, 2.75rem)" }}>
                  {meta.name}
                </h2>
                <Ornament tone={c.rule} />
                <p className={`card-lead mt-3 ${c.ink}`}>{meta.motto}</p>
                <p className="card-quote mt-5 max-w-[24rem]">
                  “{meta.question}”
                </p>

                <div className="mt-auto w-full pt-10">
                  <div className="mx-auto h-px w-full max-w-[18rem] bg-border" />
                  <div className="pt-7" data-testid={`home-score-${fw}`}>
                    <p className={`text-xs font-bold uppercase tracking-[0.18em] ${c.ink}`}>
                      {meta.scoreLabel}
                    </p>
                    <p className={`mt-3 font-display text-[4.25rem] font-semibold leading-none ${c.ink} lg:text-[5.25rem]`}
                       data-testid={`home-score-value-${fw}`}>
                      {s?.score10 ?? "—"}
                      <span className="ml-1.5 align-baseline font-sans text-2xl font-semibold text-[hsl(230_26%_34%)]">/10</span>
                    </p>
                    <p className="card-lead mt-3">
                      {s?.score10 == null ? NOT_ASSESSED : (s?.band || "Scored")}
                    </p>
                  </div>
                  <span data-testid={`home-score-open-${fw}`}
                        className={`mt-7 inline-flex w-full items-center justify-center gap-2 rounded-xl px-5 py-4 text-[15px] font-semibold leading-tight text-white transition-colors duration-300 ${c.btn}`}>
                    Enter {meta.name}
                    <ArrowUpRight size={17} className="transition-transform duration-300 group-hover:translate-x-1" />
                  </span>
                </div>
              </Link>
            );
          })}
          {access && (access?.frameworks || []).length === 0 && (
            <EmptyState
              testid="home-no-frameworks"
              title="No framework activated"
              message="No framework is both activated for this engagement and permitted for your role."
            />
          )}
        </div>
      </section>

      {/* One quiet section for everything operational */}
      <section className="panel overflow-hidden" data-testid="home-engagement">
        <button
          data-testid="home-engagement-toggle"
          onClick={() => setOpen((v) => !v)}
          className="flex w-full items-center justify-between gap-4 px-6 py-5 text-left transition-colors hover:bg-navy/[0.03]"
        >
          <span className="flex flex-wrap items-center gap-x-7 gap-y-2">
            <span className="label-caps text-goldink">Your engagement</span>
            {TABS.map(([key, label]) => (
              <span key={key} className="flex items-baseline gap-1.5" data-testid={`home-count-${key}`}>
                <span className={`font-mono text-lg tabular-nums ${
                  key === "overdue" && counts(key) ? "text-terracotta" : "text-navy"}`}>
                  {counts(key)}
                </span>
                <span className="text-[11px] text-muted-foreground">{label}</span>
              </span>
            ))}
          </span>
          <CaretDown size={14} className={`shrink-0 transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
        </button>

        {open && (
          <>
            <div className="flex gap-1 border-t border-border px-6 pt-4">
              {TABS.map(([key, label]) => (
                <button key={key} data-testid={`home-tab-${key}`} onClick={() => setTab(key)}
                        className={`rounded-t-lg px-4 py-2.5 text-[11px] uppercase tracking-[0.14em] transition-colors ${
                          tab === key ? "border-b-2 border-navy text-navy" : "text-muted-foreground hover:text-navy"}`}>
                  {label} <span className="font-mono">{counts(key)}</span>
                </button>
              ))}
            </div>

            {tab === "pending" && (
              <div className="divide-y divide-border" data-testid="home-pending-list">
                {work.items.slice(0, 8).map((i) => (
                  <div key={i.id} className="flex items-center justify-between gap-4 px-6 py-4 transition-colors hover:bg-sky/40">
                    <div className="min-w-0">
                      <p className="truncate text-sm text-navy">{i.title}</p>
                      <p className="label-caps mt-1">{i.tenant_name} · {i.item_type}</p>
                    </div>
                    <div className="flex shrink-0 items-center gap-3">
                      {i.due_date && <span className="font-mono text-xs tabular-nums text-muted-foreground">{i.due_date}</span>}
                      <StateBadge state={i.status} />
                    </div>
                  </div>
                ))}
                {work.items.length === 0 && (
                  <p className="px-6 py-10 text-sm text-muted-foreground">Nothing awaiting you.</p>
                )}
              </div>
            )}

            {tab === "overdue" && (
              <div className="divide-y divide-border" data-testid="home-exceptions-list">
                {work.exceptions.slice(0, 8).map((e) => (
                  <div key={e.id} className="border-l-[3px] border-terracotta px-6 py-4">
                    <p className="truncate text-sm text-navy">{e.title}</p>
                    <p className="mt-1 font-mono text-xs tabular-nums text-terracotta">Due {e.due_date}</p>
                  </div>
                ))}
                {work.exceptions.length === 0 && (
                  <p className="px-6 py-10 text-sm text-muted-foreground">Nothing overdue.</p>
                )}
              </div>
            )}

            {tab === "scores" && (
              <div className="divide-y divide-border" data-testid="home-recent-reports">
                {snapshots.slice(-5).reverse().map((s) => (
                  <div key={s.id} className="flex flex-wrap items-center justify-between gap-3 px-6 py-4 transition-colors hover:bg-sky/40">
                    <div>
                      <p className="text-sm text-navy">{s.cycle_name}</p>
                      <p className="label-caps mt-1">{fmtPeriod(s.period_label) || "—"}</p>
                    </div>
                    <span className="font-mono text-lg tabular-nums text-deepblue">{s.overall_score ?? "—"}</span>
                  </div>
                ))}
                {snapshots.length === 0 && (
                  <p className="px-6 py-10 text-sm text-muted-foreground">No published scores yet.</p>
                )}
              </div>
            )}
          </>
        )}
      </section>
    </div>
  );
}
